/**
 * Backend #678 / web #172: the geo vocabulary of the located catalog search, mirroring
 * `Teamstep.Base.Geo` on the wire.
 *
 * A listing's position is its seller's (decision 1), and a seller's coordinate comes with the
 * precision it was resolved at: `street` for a retailer, `postalCode` for a private seller, whose
 * street never reaches a geocoder (decision 5). A `postalCode` coordinate is a centroid about a
 * kilometre wide — a client draws it as an area, never as an exact point.
 *
 * `GeocodePrecision` is a real C# enum and arrives as a camelCase STRING (the BFF's
 * `JsonStringEnumConverter`), the same rule `ReservationStatus` taught in #168.
 */
export type GeocodePrecision = 'street' | 'postalCode';

/** A WGS 84 position. */
export interface GeoPosition {
  latitude: number;
  longitude: number;
}

/** A map viewport: `north` ≥ `south`; `west` > `east` means the box crosses the antimeridian. */
export interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Backend #678 decision 9: the searcher's position is personal data on an anonymous endpoint,
 * so it travels at about a kilometre's resolution and no finer. The BFF truncates to this many
 * decimals at its door (`GeoAreaErrors.SearcherPositionDecimals`); rounding to the same before
 * the value leaves the browser keeps the two in step and keeps the full-resolution coordinate out
 * of every access log on the way.
 */
export const SEARCHER_POSITION_DECIMALS = 2;

/** `50.93758` → `50.94`: the position as it is allowed to leave the device. */
export function roundCoordinate(value: number): number {
  const factor = 10 ** SEARCHER_POSITION_DECIMALS;
  return Math.round(value * factor) / factor;
}

/** A position rounded to {@link SEARCHER_POSITION_DECIMALS}. */
export function roundPosition(position: GeoPosition): GeoPosition {
  return {
    latitude: roundCoordinate(position.latitude),
    longitude: roundCoordinate(position.longitude),
  };
}

/**
 * The geo half of a catalog query — what `GET api/catalog/catalogitems` and `…/pins` take beside
 * the text filter (the contract in the backend's `docs/architecture/geo-catalog.md`).
 *
 * Either a radius around the origin (`origin` + `radiusKm`) or a viewport (`bounds`, with the
 * origin as an optional distance reference); never both, which the backend refuses as
 * `geoAreaAmbiguous`. An origin without a radius and without bounds is `geoAreaIncomplete`, so
 * {@link geoToHttpParams} sends nothing at all for it rather than half a filter.
 */
export interface CatalogGeoQuery {
  /** The searcher's position — the centre of the radius, or the point distances are measured from. */
  origin?: GeoPosition | null;
  /** The radius around `origin` in kilometres (1 … 200). */
  radiusKm?: number | null;
  /** The map viewport. Takes precedence over `radiusKm` when both are set. */
  bounds?: GeoBounds | null;
}

/** True when the query describes a complete area the backend will accept. */
export function hasGeoArea(geo: CatalogGeoQuery | null | undefined): boolean {
  return !!geo && (!!geo.bounds || (!!geo.origin && geo.radiusKm != null));
}

/**
 * Spreads a geo query into HttpClient params, rounding the origin on the way out. Only a complete
 * area is emitted (see {@link CatalogGeoQuery}); an incomplete one yields no parameters, so the
 * endpoint behaves exactly as it does without geo — a plain search, never a 400.
 */
export function geoToHttpParams(geo?: CatalogGeoQuery | null): Record<string, number> {
  if (!hasGeoArea(geo)) {
    return {};
  }
  const params: Record<string, number> = {};
  if (geo.origin) {
    const origin = roundPosition(geo.origin);
    params['lat'] = origin.latitude;
    params['lng'] = origin.longitude;
  }
  if (geo.bounds) {
    params['north'] = geo.bounds.north;
    params['south'] = geo.bounds.south;
    params['east'] = geo.bounds.east;
    params['west'] = geo.bounds.west;
  } else {
    params['radiusKm'] = geo.radiusKm;
  }
  return params;
}
